<div class="labour-report-container">
  <!-- Header -->
  <div class="page-header">
    <div class="header-content">
      <h2 class="page-title">
        <mat-icon>calendar_today</mat-icon>
        {{ isEditMode ? 'Edit' : 'Add New' }} Labour Mobilization Plan
      </h2>
      <div class="header-actions">
        <button type="button" class="btn-icon" (click)="showCategoryManager = !showCategoryManager" matTooltip="Manage Categories">
          <mat-icon>settings</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <!-- Category Manager Panel -->
  <div class="column-config-panel" *ngIf="showCategoryManager" [class]="'info-card'">
    <div class="card-header-modern">
      <h5 class="card-title">
        <mat-icon>group</mat-icon>
        Manage Labour Categories
      </h5>
      <button type="button" class="btn-close" (click)="showCategoryManager = false">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="card-body-modern">
      <div class="add-category-section" style="margin-bottom: 1rem;">
        <div class="add-category-input" style="display: flex; gap: 0.5rem;">
          <input type="text" class="form-control-modern" [(ngModel)]="newCategoryLabel" 
                 [ngModelOptions]="{standalone: true}"
                 placeholder="Enter category name (e.g., Electrician)" 
                 (keyup.enter)="addCategory()">
          <button type="button" class="btn-primary" (click)="addCategory()">
            <mat-icon>add</mat-icon>
            Add
          </button>
        </div>
      </div>
      <div class="categories-list-section">
        <div class="form-grid">
          <div *ngFor="let cat of labourCategories" class="category-item" style="display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0;">
            <div *ngIf="editingCategoryKey !== cat.key" class="category-display" style="flex: 1;">
              <span>{{ cat.label }}</span>
            </div>
            <div *ngIf="editingCategoryKey === cat.key" class="category-edit" style="flex: 1;">
              <input type="text" class="form-control-modern" [(ngModel)]="editingCategoryLabel" [ngModelOptions]="{standalone: true}">
            </div>
            <div class="category-actions" style="display: flex; gap: 0.25rem;">
              <button *ngIf="editingCategoryKey === cat.key" type="button" class="btn-icon" (click)="saveCategoryEdit()">
                <mat-icon>check</mat-icon>
              </button>
              <button *ngIf="editingCategoryKey === cat.key" type="button" class="btn-icon" (click)="cancelCategoryEdit()">
                <mat-icon>close</mat-icon>
              </button>
              <button *ngIf="editingCategoryKey !== cat.key" type="button" class="btn-icon" (click)="startEditCategory(cat.key)" matTooltip="Edit">
                <mat-icon>edit</mat-icon>
              </button>
              <button type="button" class="btn-icon" (click)="removeCategory(cat.key)" 
                      [disabled]="labourCategories.length <= 1" matTooltip="Remove">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <form [formGroup]="planForm" (ngSubmit)="onSubmit()">
    <!-- Basic Information Card -->
    <div class="info-card">
      <div class="card-header-modern">
        <h5 class="card-title">
          <mat-icon>info</mat-icon>
          Basic Information
        </h5>
      </div>
      <div class="card-body-modern">
        <div class="form-grid">
          <div class="form-field">
            <label class="form-label">
              Project <span class="required">*</span>
            </label>
            <select class="form-control-modern" formControlName="project_id" 
                    [class.is-invalid]="planForm.get('project_id')?.invalid && planForm.get('project_id')?.touched">
              <option value="">Select Project</option>
              <option *ngFor="let project of projects" [value]="project.id">
                {{ project.project_name }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label class="form-label">
              Plan Date <span class="required">*</span>
            </label>
            <input type="date" class="form-control-modern" formControlName="plan_date" 
                   [class.is-invalid]="planForm.get('plan_date')?.invalid && planForm.get('plan_date')?.touched">
          </div>
        </div>
      </div>
    </div>

    <!-- Labour Categories Card -->
    <div class="info-card">
      <div class="card-header-modern">
        <h5 class="card-title">
          <mat-icon>group</mat-icon>
          Labour Categories
        </h5>
      </div>
      <div class="card-body-modern">
        <div class="form-grid">
          <div class="form-field" *ngFor="let cat of labourCategories">
            <label class="form-label">{{ cat.label }}</label>
            <input type="number" class="form-control-modern" [formControlName]="cat.key" min="0" 
                   (input)="planForm.updateValueAndValidity()" placeholder="0">
          </div>
        </div>

        <div class="summary-box">
          <div class="summary-item">
            <span class="summary-label">Total Available:</span>
            <span class="summary-value">{{ calculateTotalAvailable() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Planning Information Card -->
    <div class="info-card">
      <div class="card-header-modern">
        <h5 class="card-title">
          <mat-icon>trending_up</mat-icon>
          Planning Information
        </h5>
      </div>
      <div class="card-body-modern">
        <div class="form-grid">
          <div class="form-field">
            <label class="form-label">Committed to Increase</label>
            <input type="number" class="form-control-modern" formControlName="committed_to_increase" min="0" 
                   (input)="calculateShortage()" placeholder="0">
          </div>

          <div class="form-field">
            <label class="form-label">Expected Total Strength</label>
            <input type="number" class="form-control-modern" formControlName="expected_total_strength" min="0" 
                   (input)="calculateShortage()" placeholder="0">
          </div>

          <div class="form-field">
            <label class="form-label">Net Increase</label>
            <input type="number" class="form-control-modern" formControlName="net_increase" min="0" placeholder="0">
          </div>
        </div>

        <div class="alert-box" [class.alert-success]="calculateShortage() >= 0" [class.alert-danger]="calculateShortage() < 0">
          <div class="alert-content">
            <mat-icon>{{ calculateShortage() >= 0 ? 'check_circle' : 'warning' }}</mat-icon>
            <div>
              <strong>Shortage Against Committed:</strong>
              <span class="shortage-value">{{ calculateShortage() }}</span>
            </div>
          </div>
        </div>

        <div class="form-field full-width">
          <label class="form-label">Notes</label>
          <textarea class="form-control-modern" formControlName="notes" rows="3" 
                    placeholder="Add any additional notes..."></textarea>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-footer">
      <button type="button" class="btn-secondary" (click)="cancel()">
        <mat-icon>close</mat-icon>
        Cancel
      </button>
      <button type="submit" class="btn-primary" [disabled]="loading || planForm.invalid">
        <mat-spinner *ngIf="loading" diameter="20" class="spinner-inline"></mat-spinner>
        <mat-icon *ngIf="!loading">{{ isEditMode ? 'update' : 'save' }}</mat-icon>
        {{ isEditMode ? 'Update' : 'Create' }} Plan
      </button>
    </div>
  </form>
</div>
